// Shared bits for the Zoho order import (Import Orders button and the
// timed auto-import while the Hub is open).

export interface OrderImportRun {
  id: number
  trigger: string
  started_at: string
  finished_at: string | null
  orders_pending: number | null
  orders_fetched: number | null
  orders_new: number | null
  lines_upserted: number | null
  customers_added: number | null
  skipped_lines: string[] | null
  error: string | null
}

export interface OrderImportResult {
  ran: boolean
  skippedReason: string | null
  run: OrderImportRun | null
}

export interface LastOrderImport {
  lastSuccess: OrderImportRun | null
  lastRun: OrderImportRun | null
}

// How often the Hub imports on its own while it's open.
export const AUTO_IMPORT_MINUTES = 60

// Fired on window after an import adds or changes orders, so open pages
// can reload.
export const ORDERS_IMPORTED_EVENT = 'hub:orders-imported'

export function announceOrdersImported(result: OrderImportResult) {
  window.dispatchEvent(new CustomEvent(ORDERS_IMPORTED_EVENT, { detail: result }))
}

/** "just now", "5 min ago", "2 hr ago", or a date. */
export function timeAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'never'
  const ms = now - Date.parse(iso)
  if (Number.isNaN(ms)) return 'never'
  const min = Math.floor(ms / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min} min ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} hr ago`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** One-line summary of a finished run for a toast. */
export function describeRun(run: OrderImportRun | null): string {
  if (!run) return 'Orders imported'
  const added = run.orders_new ?? 0
  const updated = Math.max(0, (run.orders_fetched ?? 0) - added)
  const parts: string[] = []
  if (added > 0) parts.push(`${added} new order${added === 1 ? '' : 's'}`)
  if (updated > 0) parts.push(`${updated} updated`)
  if (parts.length === 0) return 'No new or changed orders in Zoho'
  return `Imported ${parts.join(', ')}`
}
